import React, { useMemo } from "react";
import { User } from "../../../../types";
import { config } from "../../../../constants";
import ImgPicker from "../imgpicker";

export type Avatar = {
  id: number;
  name: string;
  premium?: boolean;
  konami?: boolean;
};

interface AvatarSelectionProps {
  spriteId: number;
  onChange: (spriteId: number) => void;
  user?: User;
  konami: boolean;
}

// Avatar names mapped to IDs (display layer)
const names: Avatar[] = [
  { id: 0, name: "ghost" },
  { id: 24, name: "cryghost", premium: true },
  { id: 1, name: "demon" },
  { id: 2, name: "raccoon" },
  { id: 3, name: "trash" },
  { id: 4, name: "dog" },
  { id: 5, name: "spike" },
  { id: 6, name: "armpit" },
  { id: 7, name: "cat" },
  { id: 8, name: "rabbit" },
  { id: 9, name: "potato" },
  { id: 25, name: "sparkletato", premium: true },
  { id: 10, name: "buffpire" },
  { id: 26, name: "buffstare", premium: true },
  { id: 11, name: "burger" },
  { id: 12, name: "dino" },
  { id: 13, name: "kitsune" },
  { id: 27, name: "glarefox", premium: true },
  { id: 14, name: "protec" },
  { id: 15, name: "ratty" },
  { id: 16, name: "reapr" },
  { id: 28, name: "reaprlash", premium: true },
  { id: 17, name: "skellybts" },
  { id: 29, name: "pumpedsklly", premium: true },
  { id: 18, name: "teagor" },
  { id: 19, name: "wormman" },
  { id: 30, name: "shinyworm", premium: true },
  { id: 20, name: "hedgee" },
  { id: 21, name: "scrambles" },
  { id: 22, name: "poulet" },
  { id: 23, name: "plat", premium: true, konami: true },
];

function getAvatars(premium?: boolean, konami?: boolean) {
  return names
    .filter((a) => premium || !a.premium)
    .filter((a) => konami || !a.konami || premium);
}

/**
 * Simplified AvatarSelection component
 * Avatar validation and filtering logic moved to usePlayerSettings hook
 * This component only handles display and user interaction
 */
const AvatarSelection: React.FC<AvatarSelectionProps> = ({
  spriteId,
  onChange,
  user,
  konami,
}) => {
  // Compute available avatars based on user status (memoized for performance)
  const curNames = useMemo(
    () => getAvatars(user?.premium, konami),
    [user?.premium, konami]
  );

  return (
    <>
      <div className="w-full max-w-md flex flex-col space-y-2">
        <div className="w-full">
          <ImgPicker
            spriteId={spriteId}
            setSpriteId={onChange}
            curNames={curNames}
          />
        </div>
        <div
          id="sprite-name"
          className={`relative text-xl leading-tight w-full bg-gray-drk shadow-[0.1rem_0.1rem_black] py-2 text-center ${
            spriteId === 22 ? 'cursor-pointer hover:underline' : ''
          }`}
          onClick={spriteId === 22 ? () => window.open(config.pouletUrl, "_blank") : undefined}
        >
          {curNames.find((c) => c.id === spriteId)?.name}
        </div>
      </div>
    </>
  );
};

export default AvatarSelection;
